/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import { Icon, Button, StyledModal, Text, Heading, Stack } from 'sarsaparilla';
import { Gnoment } from 'shared-ui/gnoment';
import PropTypes from 'prop-types';
import TempForm from './TempForm';
import PreferredDatesListContainer from '../../containers/multi-division/AvailabilityContainer';
import {
    dateSelectionDefault,
    dateSelectionProps,
} from '../../props/multi-division/DateSelection';
import { ARE_CHOICES_SELECTED, ANGELS_LANDING_FACILITIES } from '../../constants';
import {
    getValidationErrorStyle,
    getNewId,
    getDefaultPreferenceDivision,
    readRuleValue,
    hasLotteryPreferenceDateRangeGroupSize,
} from '../../utils/utilsNew';

const defaultProps = {
    max: 4,
    isLottery: false,
};
export default class PreferredDatesPicker extends Component {
    static propTypes = {
        divisions: PropTypes.shape({}),
        seasonStart: PropTypes.instanceOf(Gnoment),
        dates: PropTypes.arrayOf(dateSelectionProps),
        onChange: PropTypes.func.isRequired,
        max: PropTypes.number,
        permit: PropTypes.object,
        allowSameDate: PropTypes.bool,
        validation: PropTypes.shape({}),
        aggSeasonRules: PropTypes.array,
        isLottery: PropTypes.bool,
        isCommercialUser: PropTypes.bool,
    };

    static defaultProps = defaultProps;

    constructor(props) {
        super(props);

        this.state = {
            datesSelected: 1,
            modalIsOpen: false,
            feesModalIsOpen: false,
        };
        this.handleNumberPicked = this.handleNumberPicked.bind(this);
        this.handleNumberChanged = this.handleNumberChanged.bind(this);
        this.addSelection = this.addSelection.bind(this);
        this.removeSelection = this.removeSelection.bind(this);
        this.handleSelectionChange = this.handleSelectionChange.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.openModal = this.openModal.bind(this);
        this.openApplicableFeesModal = this.openApplicableFeesModal.bind(this);
    }

    handleNumberPicked() {
        const selection = [];
        for (let i = selection.length; i < this.state.datesSelected; i += 1) {
            selection.push(this.getNewSelection());
        }
        this.props.onChange(selection);
    }

    handleSelectionChange(index, value) {
        const selections = this.props.dates.slice();
        selections[index] = value;
        this.props.onChange(selections);
    }

    handleNumberChanged(n) {
        const max = this.props.max ? this.props.max : defaultProps.max;
        let i = n;
        if (n > max) {
            i = max;
        }
        if (n < 0) {
            i = 0;
        }
        this.setState({ datesSelected: i });
    }

    getNewSelection() {
        const { permit, isCommercialUser } = this.props;
        const defaultDivision = getDefaultPreferenceDivision(permit.id, isCommercialUser);
        return { ...dateSelectionDefault, id: getNewId(), division_id: defaultDivision };
    }

    addSelection(e) {
        e.preventDefault();
        this.props.onChange([...this.props.dates, this.getNewSelection()]);
    }

    removeSelection(index) {
        const selections = this.props.dates.slice();
        selections.splice(index, 1);
        this.props.onChange(selections);
    }

    openModal() {
        this.setState({ modalIsOpen: true });
    }

    closeModal() {
        this.setState({
            modalIsOpen: false,
        });
    }

    openApplicableFeesModal() {
        this.setState({ feesModalIsOpen: true });
    }

    closeApplicableFeesModal() {
        this.setState({ feesModalIsOpen: false });
    }

    renderApplicableFeesModal() {
        return (
            <StyledModal
                size="md"
                isOpen={this.state.feesModalIsOpen}
                heading="Applicable Fees"
                onRequestClose={this.closeApplicableFeesModal}
            >
                <Text>
                    If your application is selected through the lottery, you&apos;ll
                    automatically be awarded a permit and applicable use fees will be
                    charged to the credit or debit card used for your application. If the
                    charge to the credit or debit card is declined for any reason, the
                    reservation will be forfeited.
                </Text>
            </StyledModal>
        );
    }

    renderPreferencesInstructionText(isDateRange) {
        const max = this.props.max ? this.props.max : defaultProps.max;
        const { permit } = this.props;

        if (ANGELS_LANDING_FACILITIES.includes(permit.id)) {
            return (
                <p id="preferred-dates-text">
                    Choose your preferred dates and times below. You can apply up to{' '}
                    <b>{max} choices</b>. The more dates you add, the better your chances!
                    The start time range indicates the time you should be at the Grotto
                    trailhead. If you are awarded a permit, your credit card will
                    automatically be charged&nbsp;
                    <Button
                        appearance="link"
                        screenReaderTextBefore="Open modal for more information on"
                        onClick={() => this.openApplicableFeesModal()}
                        type="button"
                        className="per-inline-button"
                    >
                        Applicable Fees
                    </Button>
                    .
                </p>
            );
        }
        return (
            <p id="preferred-dates-text">
                Choose your preferred dates below. You can apply up to{' '}
                <b>{max} choices</b>. The more dates you add the better your chances at
                winning the lottery!
                {isDateRange && (
                    <span>
                        {' '}
                        Entry and exit dates can be the same. Max group size is{' '}
                        {readRuleValue('MaxGroupSize', permit.rules)} people.
                    </span>
                )}
            </p>
        );
    }

    renderChoiceHelpModal(isDateRange) {
        return (
            <StyledModal
                size="md"
                isOpen={this.state.modalIsOpen}
                heading="How do the lottery choices work?"
                onRequestClose={this.closeModal}
            >
                <Stack space="sm">
                    <Text>
                        You can enter several choices. The order of your choices matter.
                        When evaluating your application, the system will start with your
                        first choice. If it finds availability with that choice, it will
                        award a permit reservation. If it doesn&apos;t find availability,
                        the system moves to the next choice. If no availability is found
                        with any choice, it will consider the application unsuccessful.
                    </Text>
                    {isDateRange && (
                        <>
                            <Heading headingLevel={6} appearance="h6">
                                Single Date
                            </Heading>
                            <Text>
                                Selecting this option allows you to enter a single date
                                for your application. When evaluating your application for
                                the lottery, the system will look at the selected date for
                                availability. If there is enough availability it will
                                award a permit. If there is not enough availability if
                                will move to the next choice.
                            </Text>
                            <Heading headingLevel={6} appearance="h6">
                                First Date Available Within
                            </Heading>
                            <Text>
                                Allows you to choose a range of dates that you would be
                                willing to use for this permit. When your application is
                                evaluated, the system will start at the beginning of the
                                range to look for an available date. If a date is not
                                available, the system will look at the next date. This
                                will repeat until it finds a date with availability or it
                                reaches the end of the range.
                            </Text>
                        </>
                    )}
                </Stack>
            </StyledModal>
        );
    }

    render() {
        let body;
        const max = this.props.max ? this.props.max : defaultProps.max;
        const { permit, isCommercialUser } = this.props;
        if (this.props.dates && this.props.dates.length > 0) {
            body = (
                <PreferredDatesListContainer
                    permit={this.props.permit}
                    selections={this.props.dates}
                    allowMore={this.props.dates.length < max}
                    onChange={this.handleSelectionChange}
                    onAdd={this.addSelection}
                    onRemove={this.removeSelection}
                    divisions={this.props.divisions}
                    seasonStart={this.props.seasonStart}
                    allowSameDate={this.props.allowSameDate}
                    validation={this.props.validation}
                    aggSeasonRules={this.props.aggSeasonRules}
                    isCommercialUser={this.props.isCommercialUser}
                    isLottery={this.props.isLottery}
                />
            );
        } else {
            body = (
                <TempForm
                    onSubmit={this.handleNumberPicked}
                    onChange={this.handleNumberChanged}
                    inputClass={getValidationErrorStyle(
                        this.props.validation,
                        ARE_CHOICES_SELECTED
                    )}
                    value={this.state.datesSelected !== 0 ? this.state.datesSelected : 1}
                    minValue={1}
                    maxValue={max}
                    isLabelRequired
                />
            );
        }
        const isDateRange = hasLotteryPreferenceDateRangeGroupSize(
            permit,
            getDefaultPreferenceDivision(permit.id, isCommercialUser)
        );
        return (
            <div className="per-preferred-dates-form per-section-group">
                <div className="per-subsection-header">
                    <div className="per-subsection-title">Lottery Preferences</div>
                    <div className="per-subsection-actions">
                        <Button
                            appearance="link"
                            iconBeforeElement={<Icon iconName="help" />}
                            onClick={() => {
                                this.openModal();
                            }}
                        >
                            How do the choices work?
                        </Button>
                    </div>
                </div>
                <fieldset>
                    <legend>{this.renderPreferencesInstructionText(isDateRange)}</legend>
                    {body}
                </fieldset>
                {this.renderChoiceHelpModal(isDateRange)}
                {this.renderApplicableFeesModal()}
            </div>
        );
    }
}
